/**
 * Inline market picker for expanded game cards.
 * Market chip strip (horizontal scroll) + odds for the selected market only.
 * Matches the A1 · Inline expand list design.
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { View, Text, Pressable, ScrollView, Platform, Image } from 'react-native';
import { getInlineMarketPickerStyles } from './InlineMarketPicker.styles';
import { MarketOddsRenderer } from './MarketOddsRenderer';
import { mergeOverUnderByFamily } from '../MatchDetailsScreen/overUnderGrouping.helper';
import { MARKET_IDS, isChipFullyPremium, PREMIUM_MARKETS_ENABLED } from '../../../src/create/constants/matchDetails.constants';
import { CHIP_LABEL, SHORT_MARKET_DESC } from '../../../src/create/constants/marketLabels.constants';
import { filterMarketsForIosCreate } from '../../../src/create/helpers/premiumMarketsIosDefer.helper';
import { dropUnpriceableMarkets } from '../../../src/create/helpers/priceableMarkets.helper';
import { marketHasPick } from '../../../src/create/helpers/pickIndicators.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets';
import { Skeleton } from '../../ui/Skeleton';
import { useIsProUser, useIsProUserLoading } from '../../../src/shared/hooks/useIsProUser';
import { useDialog } from '../../../src/shared/hooks/useDialog';
import { useGuestWalkthroughStore } from '../../../src/shared/stores/guestWalkthroughStore';
import { useWalkthroughStep } from '../../../src/shared/hooks/useWalkthroughStep';
import { WALKTHROUGH_STEPS } from '../../../src/create/constants/guestWalkthrough.constants';
import type { MarketOdds, MarketOddValue } from '../../../src/create/types/create.types';

/** The home outcome of the match-winner market (value string is 'Home'). */
function findHomeValue(market: MarketOdds | null): MarketOddValue | null {
  if (market == null) return null;
  return market.values.find((v) => v.value.toLowerCase() === 'home') ?? market.values[0] ?? null;
}

const styles = getInlineMarketPickerStyles();

function chipLabel(id: number, name: string): string {
  return CHIP_LABEL[id] ?? name.slice(0, 12);
}


// ── InlineMarketPicker ────────────────────────────────────────────────────
export interface InlineMarketPickerProps {
  gameId: string;
  homeName: string;
  awayName: string;
  markets: MarketOdds[];
  isSelected: (gameId: string, marketId: number, value: string) => boolean;
  onPick: (
    gameId: string,
    marketId: number,
    marketName: string,
    value: string,
    odd: number,
    homeName: string,
    awayName: string
  ) => void;
  /**
   * Premium market IDs the user may pick even when not Pro — e.g. markets a challenge already
   * offers, which the server accepts from non-Pro invitees. Empty/undefined keeps the Pro lock.
   */
  unlockedPremiumMarketIds?: ReadonlySet<number>;
  /** True for the spotlighted walkthrough card — drives the market-tab and home-pick tour steps. */
  isWalkthroughCard?: boolean;
  /** Dot each chip that already holds a pick for this game (create build views, AC-3.10). */
  markPickedChips?: boolean;
}

export const InlineMarketPicker: React.FC<InlineMarketPickerProps> = ({
  gameId,
  homeName,
  awayName,
  markets,
  isSelected,
  onPick,
  unlockedPremiumMarketIds,
  isWalkthroughCard = false,
  markPickedChips = false,
}) => {
  const isPro = useIsProUser();
  const isProLoading = useIsProUserLoading();
  const { showAlert } = useDialog();
  // Unpriceable outcomes go before the family merge, so a merged Goals O/U chip is built only from
  // lines the user can actually bet at rather than inheriting a `0.00` tile from one of them.
  const merged = useMemo(
    () => mergeOverUnderByFamily(
      dropUnpriceableMarkets(
        Platform.OS === 'ios' ? filterMarketsForIosCreate(markets) : markets,
      ),
    ),
    [markets],
  );

  const matchWinnerMarket = useMemo(
    () => merged.find((m) => m.id === MARKET_IDS.MATCH_WINNER) ?? null,
    [merged]
  );
  // During the walkthrough the 1X2 (match winner) tab is the one we teach, so default to it.
  const [activeId, setActiveId] = useState<number | null>(
    (isWalkthroughCard ? matchWinnerMarket?.id : undefined) ?? merged[0]?.id ?? null
  );

  useEffect(() => {
    if (merged.length === 0) { setActiveId(null); return; }
    if (!merged.find((m) => m.id === activeId)) {
      setActiveId(merged[0].id);
    }
  }, [merged, activeId]);

  const activeMarket = merged.find((m) => m.id === activeId) ?? null;

  // ── Walkthrough wiring (spotlighted card only) ──────────────────────────────
  const walkthroughStep = useGuestWalkthroughStore((s) => s.currentStep);
  const advanceStep = useGuestWalkthroughStore((s) => s.advanceStep);
  const homeValue = useMemo(() => findHomeValue(matchWinnerMarket), [matchWinnerMarket]);

  const handleTabProxyTap = useCallback(() => {
    if (matchWinnerMarket) setActiveId(matchWinnerMarket.id);
    advanceStep();
  }, [matchWinnerMarket, advanceStep]);

  const handleHomePickProxyTap = useCallback(() => {
    if (matchWinnerMarket && homeValue) {
      onPick(
        gameId,
        homeValue.marketId ?? matchWinnerMarket.id,
        matchWinnerMarket.name,
        homeValue.value,
        Number(homeValue.odd) || 0,
        homeName,
        awayName
      );
    }
    advanceStep();
  }, [matchWinnerMarket, homeValue, onPick, gameId, homeName, awayName, advanceStep]);

  const { viewRef: tabRef } = useWalkthroughStep(WALKTHROUGH_STEPS.MARKET_TAB, {
    isEnabled: isWalkthroughCard && matchWinnerMarket != null,
    useAfterLayout: true,
    onProxyTap: handleTabProxyTap,
  });
  const { viewRef: homeTileRef } = useWalkthroughStep(WALKTHROUGH_STEPS.MATCH_DETAILS, {
    isEnabled: isWalkthroughCard && matchWinnerMarket != null && homeValue != null,
    useAfterLayout: true,
    onProxyTap: handleHomePickProxyTap,
  });

  const isHomePickStep =
    isWalkthroughCard &&
    walkthroughStep === WALKTHROUGH_STEPS.MATCH_DETAILS &&
    activeMarket?.id === MARKET_IDS.MATCH_WINNER;

  const handlePremiumBlock = useCallback(() => {
    showAlert({
      title: 'Pro Feature',
      message: 'Upgrade to Zporti Pro to unlock this prediction type.',
    });
  }, [showAlert]);

  const handlePick = useCallback(
    (gId: string, marketId: number, marketName: string, value: string, odd: number) => {
      onPick(gId, marketId, marketName, value, odd, homeName, awayName);
    },
    [onPick, homeName, awayName]
  );

  if (merged.length === 0) return null;

  return (
    <View style={styles.container}>
      {/* Market chip strip */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={styles.chipsRow}>
          {merged.map((mk) => {
            const sel = mk.id === activeId;
            const fullyPremium = isChipFullyPremium(mk.id);
            // Locked while Pro status is unknown so non-Pro users can't tap premium during the
            // cold-start fetch window; dim only once we KNOW they're non-Pro (avoids a lock flash
            // for confirmed-Pro users), and show a crown skeleton while resolving.
            const isLocked = fullyPremium && !isPro && !unlockedPremiumMarketIds?.has(mk.id);
            const hasPick = markPickedChips && marketHasPick(mk, gameId, isSelected);
            return (
              <Pressable
                key={mk.id}
                ref={isWalkthroughCard && mk.id === MARKET_IDS.MATCH_WINNER ? tabRef : undefined}
                style={({ pressed }) => [
                  styles.chip,
                  sel && styles.chipSelected,
                  isLocked && !isProLoading && styles.chipDimmed,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.chipPressed,
                ]}
                onPress={() => {
                  if (isLocked) {
                    if (!isProLoading) handlePremiumBlock();
                    return;
                  }
                  setActiveId(mk.id);
                }}
                accessibilityRole="tab"
                accessibilityState={{ selected: sel }}
                accessibilityHint={hasPick ? 'Has a pick' : undefined}
              >
                <Text style={[styles.chipText, sel && styles.chipTextSelected]}>
                  {chipLabel(mk.id, mk.name)}
                </Text>
                {hasPick && (
                  <View
                    style={[styles.chipPickedDot, sel && styles.chipPickedDotSelected]}
                    testID={`market-chip-picked-${mk.id}`}
                  />
                )}
                {fullyPremium && PREMIUM_MARKETS_ENABLED && (
                  isProLoading ? (
                    <Skeleton width={10} height={10} borderRadius={5} />
                  ) : (
                    <Image
                      source={{ uri: getCdnUrlExactKey(ASSET_PATHS.CROWN_ICON) }}
                      style={[styles.chipCrown, sel && styles.chipCrownSelected]}
                      resizeMode="contain"
                    />
                  )
                )}
              </Pressable>
            );
          })}
        </View>
      </ScrollView>

      {/* Short description for active market */}
      {activeMarket && SHORT_MARKET_DESC[activeMarket.id] != null && (
        <Text style={styles.marketDesc}>{SHORT_MARKET_DESC[activeMarket.id]}</Text>
      )}

      {/* Active market odds */}
      {activeMarket && (
        <MarketOddsRenderer
          market={activeMarket}
          gameId={gameId}
          homeName={homeName}
          awayName={awayName}
          isSelected={isSelected}
          onPick={handlePick}
          isPro={isPro}
          onPremiumBlock={handlePremiumBlock}
          unlockedPremiumMarketIds={unlockedPremiumMarketIds}
          spotlightValue={isHomePickStep ? homeValue?.value ?? null : null}
          spotlightTileRef={isHomePickStep ? homeTileRef : undefined}
        />
      )}
    </View>
  );
};
